/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-len */

import CloseIcon from '@mui/icons-material/CloseOutlined';
import WarningTwoToneIcon from '@mui/icons-material/WarningTwoTone';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Link, Pagination, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import React, { useEffect, useState } from 'react';
import { IShortageBoardOptions, IShortageCandidateInfo, IShortageListFilter, IShortageListItem } from "../../routes/doa-forecast-doa-admin-stats/DoaForecastStatsData";
import { StatusType } from '../../routes/doa-forecast-doa-admin-stats/DoaForecastStatsModels';
import { useSameWidthAsPreviousElement } from '../../utils/hooks/useSameWidthAsPreviousElement';
import { ShortageFilter } from './ShortageFilter';
import {
  boardPlateStyle,
  boardRowContainerStyle,
  boardRowStyle,
  calledStyle,
  checkBoxStyle,
  closeButtonStyle,
  headerCellStyle,
  jobDetailsButtonStyle,
  makeAvailableStyle,
  noDataStyle,
  paginationStyles,
  phoneStyle,
  regularWorkForceStyle,
  shiftPecentageText,
  spaceRight,
  squamishPlateStyle,
  warningIconStyle
} from './styled';

export interface IShortagesTableProps {
  isDoaAdmin: boolean,
  isFulfilled: boolean,
  list?: IShortageListItem[],
  currentJobId: number,
  currentFlag545: number,
  boardOptions: IShortageBoardOptions[],
  redBorderClassName: string,
  filter: IShortageListFilter,
  loading: boolean,
  submitting?: boolean,
  onFilterUpdate: (updatedFilter: IShortageListFilter) => void;
  onUpdateCandidateStatuses: (
    jobId: number,
    flag545: number,
    boardCode: string,
    regId: number,
    statusType: StatusType,
    status: boolean
  ) => void;
  onSetOpenCallbackDetails: (boardCode: string, regId: number) => void;
  onOpenClosePlateModal: (regId: number, board: string) => void;
  onClose: () => void;
  callbackInfo: { boardCode: string; regId: number }[];
}

interface IBlockActionsProps {
  boardIndex: number,
  candidateIndex: number,
  statusType: StatusType
}

const renderPhone = (phoneNumber: string | null, label: string) => {
  if (phoneNumber) {
    return (
      <Typography sx={phoneStyle}>
        <Typography component={'span'}>
          {label}: <Link href={`tel:${phoneNumber}`}>{phoneNumber}</Link>
        </Typography> 
      </Typography>
    );
  }
  return (
    <Typography sx={phoneStyle}>
      {label}: --
    </Typography>
  );
}

const renderLastWorked = (date: string | null) => {
  return date ? <Typography sx={{ textAlign: 'left' }}>Last Worked: {date}</Typography> : null;
}

const renderLastShift = (shift: string | null) => {
  return shift ? <Typography sx={{ textAlign: 'left' }}>({shift})</Typography> : null;
}

const renderButtonInfo = (image: string | null, desc: string | null, plate: number | null) => {
  if (image && desc && plate) {
    return (
      <Box component={'span'} sx={{ marginRight: '8px' }}>
        <img
          src={`/assets/images/buttons/${image}`}
          style={{ border: "#000000 1px solid", marginRight: '8px' }}
          alt='button'
        />
        <Typography component={'span'} sx={spaceRight}>{desc}</Typography>
        <Typography component={'span'}>(Plate: {plate})</Typography>
      </Box>
    )
  }
  return null;
}

const getButtonStyle = (ci: any) => {
  if (ci.isInSquamishComplement && !ci.isRwf) {
    return squamishPlateStyle;
  } else if (!ci.isInSquamishComplement && ci.isRwf) {
    return regularWorkForceStyle;
  } else {
    return boardPlateStyle;
  }
}

const renderButton = (ci: any, onOpenClosePlateModal: any) => {
  const buttonStyle = getButtonStyle(ci);
  return (
    <Button
      sx={buttonStyle}
      onClick={() => onOpenClosePlateModal(ci.registrationNumber, ci.boardCode)}
    >
      {ci.boardCode}{ci.plate}
    </Button>
  );
}

export const ShortagesTable: React.FC<IShortagesTableProps> = ({
  isDoaAdmin,
  isFulfilled,
  list,
  currentJobId,
  currentFlag545,
  boardOptions,
  redBorderClassName,
  filter,
  loading,
  submitting = false,
  onFilterUpdate,
  onUpdateCandidateStatuses,
  onOpenClosePlateModal,
  onSetOpenCallbackDetails,
  onClose,
  callbackInfo
}) => {
  const [blockActions, setBlockActions] = useState<IBlockActionsProps | null>(null);
  const [page, setPage] = useState<number>(1);
  const [elementRef] = useSameWidthAsPreviousElement();

  useEffect(() => {
    if (!submitting && blockActions != null) setBlockActions(null);
  }, [submitting]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const rowsPerPage = 10;
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const rows = list?.map(b => b.candidateInfo.length).reduce((sum, c) => sum + c, 0);
  const paginatedCandidates = list?.flatMap(board => board.candidateInfo).map(c => c.registrationNumber).slice(startIndex, endIndex);
  let paginatedData = list?.map(board => ({
    ...board,
    candidateInfo: board.candidateInfo.filter(
      candidate => paginatedCandidates?.some(regId => candidate.registrationNumber === regId)),
  })).filter(board => board.candidateInfo.length > 0);
  if (paginatedData?.length === 0) {
    paginatedData = [{
      boardDescription: "",
      boardCode: "",
      buttonInfo: [],
      isCasual: false,
      sortBoardPosition: 0,
      candidateInfo: []
    }]
  }
  const totalPages = rows ? Math.ceil(rows / rowsPerPage) : 0;
  const onPageChange = (_event: React.ChangeEvent<unknown>, page: number) => setPage(page);

  const onClickAction = (
    boardIndex: number,
    candidateIndex: number,
    statusType: StatusType,
    action: () => void) => {
    setBlockActions({ boardIndex, candidateIndex, statusType });
    action();
  }

  const renderMakeAvailable = (
    candidateIndex: number,
    itemIndex: number,
    blockActions: IBlockActionsProps | null,
    candidate: IShortageCandidateInfo,
    boardCode: string
  ) => {
    // render loading state
    if (
      blockActions &&
      blockActions.boardIndex === itemIndex &&
      blockActions.candidateIndex === candidateIndex &&
      blockActions.statusType === StatusType.available
    ) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
          <CircularProgress />
        </Box>
      );
    }
    // render blank component 
    if (isFulfilled && !candidate.isAvailable) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: '28px' }} />
      );
    }
    // render make available button
    return (
      <Typography
        component='button'
        sx={makeAvailableStyle}
        disabled={blockActions != null}
        onClick={() => onClickAction(
          itemIndex, candidateIndex, StatusType.available,
          () => onUpdateCandidateStatuses(
            currentJobId,
            currentFlag545,
            boardCode,
            candidate.registrationNumber,
            StatusType.available,
            !(candidate.isAvailable || false)
          ))}
      >
        Make {candidate.isAvailable ? 'Unavailable' : 'Available'}
      </Typography>
    );
  }

  return (
    <TableRow className={`${redBorderClassName} subTableHeadRow`}>
      <TableCell className='noPadding' colSpan={isDoaAdmin ? 9 : 7}>
        {!loading &&
          <Table size="custom" className='shortageTable'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: 'relative' }} colSpan={4}>
                  <Box sx={{ ...headerCellStyle, justifyContent: 'space-between' }}>
                    Possible Candidates to Fill the Required Jobs
                    <Box sx={closeButtonStyle} onClick={onClose}><CloseIcon /></Box>
                  </Box>
                  <ShortageFilter
                    filter={filter}
                    boardOptions={boardOptions}
                    onFilterUpdate={onFilterUpdate}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData?.map((item, i) => (
                <TableRow key={i}>
                  <TableCell className='candidateList'>
                    <Table size="custom">
                      <TableHead>
                        <TableRow>
                          <TableCell className='noBorder' sx={boardRowStyle} colSpan={5}>
                            <Box sx={boardRowContainerStyle}>
                              <Box>
                                <Typography component={'span'} sx={spaceRight}>{item.boardDescription}</Typography>
                                {item.buttonInfo?.map((button, b) => (
                                  <Typography component={'span'} key={b}>{renderButtonInfo(button.buttonImage, button.buttonDescription, button.plateNumber)}</Typography>
                                ))}
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {item.candidateInfo.length === 0 &&
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Box sx={noDataStyle}>
                                <Box sx={{ textAlign: 'center' }}>No data</Box>
                              </Box>
                            </TableCell>
                          </TableRow>
                        }
                        {item.candidateInfo.map((candidate, c) => (
                          <React.Fragment key={c}>
                            {candidate.errorMsg ?
                              <TableRow className='erorrMsg'>
                                <TableCell colSpan={5}>
                                  {candidate.errorMsg}
                                </TableCell>
                              </TableRow> :
                              <TableRow className='candidateRows'>
                                <TableCell
                                  sx={{ height: 0 }}
                                  className={`shortageListFirstCell ${candidate.isAvailable ? 'greenBackground' : ''}`}
                                >
                                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', flex: 2, justifyContent: 'flex-start', minWidth: '158px' }}>
                                      {renderMakeAvailable(c, i, blockActions, candidate, item.boardCode)}
                                    </Box>
                                    <Box sx={{ flex: 1 }}
                                      className={`
                                        ${candidate.hasBeenContacted && !candidate.isAvailable && 'redBackground'}
                                      `}> {blockActions?.boardIndex === i &&
                                        blockActions?.candidateIndex === c &&
                                        blockActions.statusType === StatusType.contacted ?
                                        <Box sx={{ display: "flex", justifyContent: "center", p: "17px" }}>
                                          <CircularProgress />
                                        </Box> :
                                        <Box>
                                          <FormControlLabel
                                            sx={calledStyle}
                                            label="Called"
                                            disabled={candidate.isAvailable}
                                            control={
                                              <Checkbox
                                                sx={checkBoxStyle}
                                                checked={candidate.hasBeenContacted}
                                                disabled={blockActions != null}
                                                onClick={() => onClickAction(
                                                  i, c, StatusType.contacted,
                                                  () => onUpdateCandidateStatuses(
                                                    currentJobId,
                                                    currentFlag545,
                                                    item.boardCode,
                                                    candidate.registrationNumber,
                                                    StatusType.contacted,
                                                    !candidate.hasBeenContacted
                                                  ))}
                                              />
                                            }
                                          />
                                        </Box>
                                      }</Box>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ background: candidate.insertColor, width: '30%', position: 'relative' }}>
                                  <Typography sx={{ textAlign: 'left' }}>
                                    {candidate.employeeFullName}
                                    {
                                      candidate.isCallback && <Button
                                        sx={jobDetailsButtonStyle}
                                        onClick={() => onSetOpenCallbackDetails(candidate.boardCode, candidate.registrationNumber)}
                                      >
                                        CB
                                      </Button>
                                    }
                                  </Typography>
                                  {renderLastWorked(candidate.lastOrderDate)}
                                  {renderLastShift(candidate.lastDespatchShiftDescription)}
                                  {candidate.epdSuspensionLevelDescription && <Typography sx={{ textAlign: 'left' }}>
                                    <WarningTwoToneIcon sx={warningIconStyle} />
                                    {candidate.epdSuspensionLevelDescription}
                                  </Typography>}
                                  {candidate.isRwf && <Typography sx={{ textAlign: 'left' }}>RWF</Typography>}
                                  {candidate.isInSquamishComplement && <Typography sx={{ textAlign: 'left' }}>SQUAMISH COMPLEMENT</Typography>}
                                </TableCell>
                                <TableCell>
                                  <Typography component={'span'} sx={shiftPecentageText}>
                                    <Typography sx={{ textAlign: 'left' }}>G-Yard:</Typography>
                                    <Typography sx={{ textAlign: 'right' }}>{candidate.graveyardShiftPercentage}%</Typography>
                                  </Typography>
                                  <Typography component={'span'} sx={shiftPecentageText}>
                                    <Typography sx={{ textAlign: 'left' }}>Day:</Typography>
                                    <Typography sx={{ textAlign: 'right' }}>{candidate.dayShiftPercentage}%</Typography>
                                  </Typography>
                                  <Typography component={'span'} sx={shiftPecentageText}>
                                    <Typography sx={{ textAlign: 'left' }}>Night:</Typography>
                                    <Typography sx={{ textAlign: 'right' }}>{candidate.nightShiftPercentage}%</Typography>
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography>{candidate.registrationNumber}</Typography>
                                  {renderButton(candidate, onOpenClosePlateModal)}
                                </TableCell>
                                <TableCell>
                                  {renderPhone(candidate?.phonePrimary, 'P')}
                                  {renderPhone(candidate?.phoneAlternate, 'A')}
                                  {renderPhone(candidate?.phoneCell, 'C')}
                                </TableCell>
                              </TableRow>
                            }
                            {callbackInfo.find(ci => ci.boardCode === candidate.boardCode && ci.regId === candidate.registrationNumber) &&
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography>Callback Details:</Typography>
                                  <Typography>{candidate.callbackDetails}</Typography>
                                </TableCell>
                              </TableRow>}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        }
        {!loading && totalPages > 1 && <Pagination
          ref={elementRef}
          sx={paginationStyles}
          onChange={onPageChange}
          color="primary"
          page={page}
          count={totalPages}
          variant="outlined"
          shape="rounded"
        />}
      </TableCell>
    </TableRow >)
}   